import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';

type Language = 'en' | 'fr';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  toggleLang: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'portfolio_lang';

const resources: Record<Language, Record<string, string>> = {
  en: {
    // Navbar & layout
    'nav.brand': 'Portfolio',
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.projects': 'Projects',
    'nav.skills': 'Skills',
    'nav.contact': 'Contact',
    'nav.lang.en': 'EN',
    'nav.lang.fr': 'FR',
    'nav.lang.toggleLabel': 'Toggle language',

    // Footer
    'footer.tagline':
      'Crafting innovative digital experiences with modern technologies and design principles.',
    'footer.quickLinks': 'Quick Links',
    'footer.home': 'Home',
    'footer.about': 'About',
    'footer.projects': 'Projects',
    'footer.skills': 'Skills',
    'footer.contact': 'Contact',
    'footer.getInTouch': 'Get in Touch',
    'footer.viewResume': 'View Resume',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.copyright': 'All rights reserved.',

    // Home page
    'home.seo.title': 'Home',
    'home.seo.description':
      "Welcome to my portfolio - I'm a passionate web developer specializing in creating modern, user-friendly applications.",
    'home.hero.headingPrefix': "Hi, I'm",
    'home.hero.role': 'Web Developer & Designer',
    'home.hero.description.line1':
      'I create elegant, responsive, and user-friendly web applications using modern technologies.',
    'home.hero.description.line2':
      'With a focus on both functionality and aesthetics, I deliver solutions that make an impact.',
    'home.hero.cta.viewWork': 'View My Work',
    'home.hero.cta.contact': 'Get in Touch',
    'home.featured.title': 'Featured Projects',
    'home.featured.description':
      'Here are some of my recent projects that showcase my skills and passion for creating exceptional web applications.',
    'home.featured.viewAll': 'View All Projects',
    'home.aboutQuick.title': 'About Me',
    'home.aboutQuick.text':
      'I am a dedicated web developer specializing in the MERN stack, with a strong focus on building responsive, user‑friendly applications. Leveraging my background in design and keen attention to detail, I combine technical expertise with creative vision to deliver solutions that are both functional and visually compelling.',
    'home.aboutQuick.link': 'Learn more about me',
    'home.expertiseQuick.title': 'My Expertise',
    'home.expertiseQuick.frontend': 'Frontend Development (React, Next.js, Tailwind CSS)',
    'home.expertiseQuick.backend': 'Backend Development (Node.js, Express, MongoDB)',
    'home.expertiseQuick.uiux': 'Responsive UI/UX Design',
    'home.expertiseQuick.api': 'API Development and Integration',
    'home.expertiseQuick.link': 'View all my skills',
    'home.cta.title': 'Ready to Work Together?',
    'home.cta.text':
      "I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision.",
    'home.cta.button': "Let's Connect",

    // About page
    'about.seo.title': 'About Me',
    'about.seo.description':
      'Learn more about my background, experience, and journey as a web developer.',
    'about.title': 'About Me',
    'about.quickInfo.title': 'Quick Info',
    'about.quickInfo.location': 'Tunisia, Nabeul',
    'about.quickInfo.available': 'Available for new projects',
    'about.quickInfo.viewResume': 'View Resume',
    'about.journey.title': 'My Journey',
    'about.journey.p1':
      'I’m Asma, a Full-Stack MERN Developer focused on building fast, scalable, and user-centered web applications. Proficient in React, Node.js, Express, MongoDB, TypeScript, and modern tooling (Vite, Tailwind CSS, Redux Toolkit), I deliver end-to-end solutions—from secure RESTful APIs and database architecture to pixel-perfect, responsive frontends.',
    'about.journey.p2':
      'My approach combines clean code, performance optimization, and attention to detail with a commitment to writing maintainable, production-ready systems. Currently expanding my expertise in real-time features, advanced state management, and cloud deployment while continuously integrating the latest industry best practices.',
    'about.journey.p3':
      'Outside work, I train MMA, which sharpens the same discipline and focus I apply to every line of code.',
    'about.values.title': 'What I Believe In',
    'about.values.cleanCode': 'Clean, maintainable code that stands the test of time',
    'about.values.userCentered': 'User-centered design that prioritizes experience',
    'about.values.learning': 'Continuous learning and adapting to new technologies',
    'about.values.accessible': 'Accessible and inclusive digital products for everyone',
    'about.experience.title': 'Professional Experience',
    'about.experience.role': 'Freelance Web Developer',
    'about.experience.company': 'Freelance',
    'about.experience.period': 'May 2025 - Present',
    'about.experience.description':
      'Building responsive and scalable web applications using the MERN stack (MongoDB, Express.js, React.js, Node.js) and Next.js. Open to front-end and full-stack projects, collaborating with clients to craft modern, user-friendly digital solutions.',
    'about.education.title': 'Education',
    'about.education.degree': 'Full-Stack MERN Bootcamp',
    'about.education.institution': 'GoMyCode',
    'about.education.period': '2024 - 2025',
    'about.education.description':
      'Mastered MongoDB, Express.js, React.js, Node.js, plus advanced Next.js techniques for scalable web applications.',
    'about.cta.title': 'Interested in working together?',
    'about.cta.text':
      "I'm always looking for new and exciting projects. Let's discuss how we can collaborate to bring your ideas to life.",
    'about.cta.button': 'Get in Touch',

    // Projects page
    'projects.seo.title': 'Projects',
    'projects.seo.description':
      'Explore my portfolio of web development projects, including websites, web applications, and more.',
    'projects.title': 'My Projects',
    'projects.description':
      'Explore my latest work. Each project represents a unique challenge and showcases different aspects of my skills and expertise.',
    'projects.filter.all': 'All Projects',
    'projects.filter.featured': 'Featured',
    'projects.empty.title': 'No projects found',
    'projects.empty.text':
      'No projects match the selected filter. Try selecting a different filter.',

    // Project detail
    'projectDetail.back': 'Back to Projects',
    'projectDetail.featuredBadge': 'Featured Project',
    'projectDetail.techTitle': 'Technologies Used',
    'projectDetail.liveDemo': 'View Live Demo',
    'projectDetail.liveVersion': 'Live Version',
    'projectDetail.viewSource': 'View Source Code',
    'projectDetail.otherProjects': 'Other Projects',

    // Project card
    'projectCard.featured': 'Featured',
    'projectCard.liveDemo': 'Live Demo',
    'projectCard.liveVersion': 'Live Version',
    'projectCard.code': 'Code',
    'projectCard.viewDetails': 'View Details',

    // Skills page
    'skills.seo.title': 'Skills',
    'skills.seo.description':
      'Explore my technical skills and expertise in web development, including frontend, backend, design, and more.',
    'skills.title': 'My Skills & Expertise',
    'skills.description':
      "I've developed a diverse set of skills throughout my career. Here's an overview of my technical expertise and proficiency in various areas of web development.",
    'skills.category.frontend': 'Frontend Development',
    'skills.category.frontendDesc': 'Building responsive and interactive user interfaces',
    'skills.category.backend': 'Backend Development',
    'skills.category.backendDesc':
      'Creating robust server-side applications and APIs',
    'skills.category.design': 'Design',
    'skills.category.designDesc':
      'Creating beautiful and functional user experiences',
    'skills.category.tools': 'Tools & Methodologies',
    'skills.category.toolsDesc':
      'Supporting the development process with essential tools',
    'skills.category.productivity': 'Productivity Tools',
    'skills.category.productivityDesc':
      'Streamlining daily workflows with versatile office applications',
    'skills.learning.title': 'Currently Learning',
    'skills.learning.intro':
      "I believe in continuous learning and staying up-to-date with the latest technologies. Here are some areas I'm currently focusing on:",
    'skills.learning.web3.title': 'Web 3.0 Technologies',
    'skills.learning.web3.text':
      'Exploring blockchain, smart contracts, and decentralized applications.',
    'skills.learning.animation.title': 'Advanced Animation',
    'skills.learning.animation.text':
      'Mastering complex animations and micro-interactions for enhanced UX.',
    'skills.learning.ai.title': 'AI Integration',
    'skills.learning.ai.text':
      'Implementing AI capabilities into web applications for smarter experiences.',

    // Contact page
    'contact.seo.title': 'Contact',
    'contact.seo.description':
      'Get in touch with me regarding collaboration, projects, or any questions you might have.',
    'contact.title': "Let's Connect",
    'contact.intro':
      "Have a project in mind or want to discuss a potential collaboration? I'd love to hear from you. Fill out the form below or reach out directly through any of the provided contact methods.",
    'contact.info.title': 'Contact Information',
    'contact.info.email': 'Email',
    'contact.info.phone': 'Phone',
    'contact.info.location': 'Location',
    'contact.social.title': 'Connect on Social Media',
    'contact.availability.title': 'Availability',
    'contact.availability.text':
      'Currently available for freelance projects and full-time opportunities.',
    'contact.availability.responseTime': 'Response time:',
    'contact.availability.responseTimeValue': 'Within 24 hours',
    'contact.faq.title': 'Frequently Asked Questions',
    'contact.faq.q1': 'What services do you offer?',
    'contact.faq.a1':
      'I specialize in full-stack web development, including frontend and backend development, responsive design, API integration, and content management systems.',
    'contact.faq.q2': 'What is your typical project timeline?',
    'contact.faq.a2':
      'Project timelines vary based on complexity and scope. A simple website might take 2-3 weeks, while a complex web application could take 2-3 months. I will provide a detailed timeline during our initial consultation.',
    'contact.faq.q3': 'Do you offer maintenance services?',
    'contact.faq.a3':
      'Yes, I offer ongoing maintenance and support services to ensure your website or application remains up-to-date, secure, and functioning optimally.',
    'contact.faq.q4': 'How do you handle project revisions?',
    'contact.faq.a4':
      'My process includes regular check-ins and feedback sessions. I typically include up to three rounds of revisions in my project quotes to ensure your complete satisfaction.',

    // Contact form
    'form.getInTouch': 'Get in Touch',
    'form.sending': 'Sending message...',
    'form.sendAnother': 'Send another message',
    'form.success': 'Message sent successfully! I will get back to you soon.',
    'form.error': 'Failed to send message. Please try again later.',
    'form.name': 'Name',
    'form.name.placeholder': 'Your name',
    'form.email': 'Email',
    'form.email.placeholder': 'your.email@example.com',
    'form.subject': 'Subject',
    'form.subject.placeholder': 'Subject of your message',
    'form.message': 'Message',
    'form.message.placeholder': 'Your message here...',
    'form.submit': 'Send Message',

    // 404
    'notFound.seo.title': 'Page Not Found',
    'notFound.seo.description': "The page you're looking for could not be found.",
    'notFound.title': 'Page Not Found',
    'notFound.text': "The page you're looking for doesn't exist or has been moved.",
    'notFound.backHome': 'Back to Home',
    'notFound.goBack': 'Go Back',

    // Terms
    'terms.seo.title': 'Terms of Service',
    'terms.seo.description':
      'Terms of Service for our portfolio website. Understand the rules and guidelines for using our services.',
    'terms.title': 'Terms of Service',
    'terms.agreement.title': 'Agreement to Terms',
    'terms.agreement.text':
      'By accessing and using this website, you agree to be bound by these Terms of Service and comply with all applicable laws and regulations.',
    'terms.license.title': 'Use License',
    'terms.license.text1':
      'Permission is granted to temporarily view the materials (information or software) on our website for personal, non-commercial use only. This is the grant of a license, not a transfer of title.',
    'terms.license.text2': 'Under this license, you may not:',
    'terms.license.item1': 'Modify or copy the materials',
    'terms.license.item2': 'Use the materials for any commercial purpose',
    'terms.license.item3': 'Attempt to decompile or reverse engineer any software',
    'terms.license.item4': 'Remove any copyright or other proprietary notations',
    'terms.license.item5': 'Transfer the materials to another person',
    'terms.disclaimer.title': 'Disclaimer',
    'terms.disclaimer.text':
      "The materials on our website are provided on an 'as is' basis. We make no warranties, expressed or implied, and hereby disclaim and negate all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property.",
    'terms.limitations.title': 'Limitations',
    'terms.limitations.text':
      'In no event shall we or our suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use our website and materials.',
    'terms.links.title': 'Links',
    'terms.links.text':
      'We have not reviewed all of the sites linked to our website and are not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by us of the site. Use of any such linked website is at the user\'s own risk.',
    'terms.modifications.title': 'Modifications',
    'terms.modifications.text':
      'We may revise these Terms of Service at any time without notice. By using this website, you agree to be bound by the current version of these Terms of Service.',
    'terms.contact.title': 'Contact Information',
    'terms.contact.text':
      'If you have any questions about these Terms of Service, please contact us at',

    // Privacy
    'privacy.seo.title': 'Privacy Policy',
    'privacy.seo.description':
      'Privacy Policy for our portfolio website. Learn how we collect, use, and protect your personal information.',
    'privacy.title': 'Privacy Policy',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.text':
      'This Privacy Policy explains how we collect, use, and protect your personal information when you use our website. We are committed to ensuring the privacy and security of your data.',
    'privacy.collect.title': 'Information We Collect',
    'privacy.collect.text':
      'We collect information that you provide directly to us, including:',
    'privacy.collect.item1': 'Name and email address when you contact us',
    'privacy.collect.item2': 'Messages and communications you send through our contact form',
    'privacy.collect.item3': 'Usage data and analytics information',
    'privacy.use.title': 'How We Use Your Information',
    'privacy.use.text': 'We use the collected information to:',
    'privacy.use.item1': 'Respond to your inquiries and communications',
    'privacy.use.item2': 'Improve our website and services',
    'privacy.use.item3': 'Send you updates and newsletters (with your consent)',
    'privacy.use.item4': 'Comply with legal obligations',
    'privacy.protect.title': 'Data Protection',
    'privacy.protect.text':
      'We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Your data is stored securely and accessed only by authorized personnel.',
    'privacy.rights.title': 'Your Rights',
    'privacy.rights.text': 'You have the right to:',
    'privacy.rights.item1': 'Access your personal information',
    'privacy.rights.item2': 'Request correction of inaccurate data',
    'privacy.rights.item3': 'Request deletion of your data',
    'privacy.rights.item4': 'Withdraw your consent at any time',
    'privacy.contact.title': 'Contact Us',
    'privacy.contact.text':
      'If you have any questions about this Privacy Policy, please contact us at',
    'privacy.updates.title': 'Updates to This Policy',
    'privacy.updates.text':
      'We may update this Privacy Policy from time to time. The latest version will always be posted on this page, with the effective date clearly displayed.',
  },
  fr: {
    // Navbar & layout
    'nav.brand': 'Portfolio',
    'nav.home': 'Accueil',
    'nav.about': 'À propos',
    'nav.projects': 'Projets',
    'nav.skills': 'Compétences',
    'nav.contact': 'Contact',
    'nav.lang.en': 'EN',
    'nav.lang.fr': 'FR',
    'nav.lang.toggleLabel': 'Changer de langue',

    // Footer
    'footer.tagline':
      "Je crée des expériences digitales innovantes en m'appuyant sur des technologies modernes et des principes de design soignés.",
    'footer.quickLinks': 'Liens rapides',
    'footer.home': 'Accueil',
    'footer.about': 'À propos',
    'footer.projects': 'Projets',
    'footer.skills': 'Compétences',
    'footer.contact': 'Contact',
    'footer.getInTouch': 'Me contacter',
    'footer.viewResume': 'Voir le CV',
    'footer.privacy': 'Politique de confidentialité',
    'footer.terms': "Conditions d’utilisation",
    'footer.copyright': 'Tous droits réservés.',

    // Home page
    'home.seo.title': 'Accueil',
    'home.seo.description':
      'Bienvenue sur mon portfolio – Développeuse web passionnée, spécialisée dans la création d’applications modernes et intuitives.',
    'home.hero.headingPrefix': 'Bonjour, je suis',
    'home.hero.role': 'Développeuse Web & Designer',
    'home.hero.description.line1':
      'Je conçois des applications web élégantes, réactives et faciles à utiliser grâce aux technologies modernes.',
    'home.hero.description.line2':
      "En alliant fonctionnalité et esthétique, je livre des solutions qui ont un réel impact.",
    'home.hero.cta.viewWork': 'Voir mes projets',
    'home.hero.cta.contact': 'Me contacter',
    'home.featured.title': 'Projets mis en avant',
    'home.featured.description':
      'Voici quelques projets récents qui illustrent mes compétences et ma passion pour le développement d’applications web de qualité.',
    'home.featured.viewAll': 'Voir tous les projets',
    'home.aboutQuick.title': 'À propos de moi',
    'home.aboutQuick.text':
      'Je suis une développeuse web spécialisée dans la stack MERN, avec une forte orientation vers la création d’applications réactives et intuitives. Grâce à mon sens du design et à mon attention au détail, je combine expertise technique et créativité pour proposer des solutions à la fois fonctionnelles et esthétiques.',
    'home.aboutQuick.link': 'En savoir plus sur moi',
    'home.expertiseQuick.title': 'Mon expertise',
    'home.expertiseQuick.frontend':
      'Développement Frontend (React, Next.js, Tailwind CSS)',
    'home.expertiseQuick.backend':
      'Développement Backend (Node.js, Express, MongoDB)',
    'home.expertiseQuick.uiux': 'Conception UI/UX responsive',
    'home.expertiseQuick.api': 'Développement et intégration d’API',
    'home.expertiseQuick.link': 'Voir toutes mes compétences',
    'home.cta.title': 'Prêt(e) à collaborer ?',
    'home.cta.text':
      "Je suis toujours ouverte à de nouveaux projets, idées créatives ou opportunités de collaboration.",
    'home.cta.button': 'Discutons-en',

    // About page
    'about.seo.title': 'À propos de moi',
    'about.seo.description':
      'Découvrez mon parcours, mon expérience et mon évolution en tant que développeuse web.',
    'about.title': 'À propos de moi',
    'about.quickInfo.title': 'Informations clés',
    'about.quickInfo.location': 'Tunisie, Nabeul',
    'about.quickInfo.available': 'Disponible pour de nouveaux projets',
    'about.quickInfo.viewResume': 'Voir le CV',
    'about.journey.title': 'Mon parcours',
    'about.journey.p1':
      'Je suis Asma, développeuse Full-Stack MERN, spécialisée dans la création d’applications web rapides, scalables et centrées sur l’utilisateur. Maîtrisant React, Node.js, Express, MongoDB, TypeScript et des outils modernes (Vite, Tailwind CSS, Redux Toolkit), je conçois des solutions complètes, des APIs sécurisées aux interfaces réactives et soignées.',
    'about.journey.p2':
      'Mon approche repose sur un code propre, des performances optimisées et une grande rigueur, avec l’objectif de livrer des systèmes maintenables et prêts pour la production. J’approfondis actuellement les fonctionnalités temps réel, la gestion avancée de l’état et le déploiement cloud, tout en restant alignée sur les meilleures pratiques du secteur.',
    'about.journey.p3':
      "En dehors du développement, je pratique le MMA, une discipline qui renforce la concentration et la persévérance que j’applique également à mon travail.",
    'about.values.title': 'Ce en quoi je crois',
    'about.values.cleanCode':
      'Un code propre et maintenable qui résiste au temps',
    'about.values.userCentered':
      "Un design centré sur l’utilisateur et son expérience",
    'about.values.learning':
      'Un apprentissage continu et une adaptation aux nouvelles technologies',
    'about.values.accessible':
      'Des produits digitaux accessibles et inclusifs pour tous',
    'about.experience.title': 'Expérience professionnelle',
    'about.experience.role': 'Développeuse Web Freelance',
    'about.experience.company': 'Freelance',
    'about.experience.period': 'Mai 2025 - Présent',
    'about.experience.description':
      'Création d’applications web responsives et évolutives avec la stack MERN (MongoDB, Express.js, React.js, Node.js) et Next.js. Ouverte aux projets front-end et full-stack, en collaboration avec des clients pour concevoir des solutions modernes et intuitives.',
    'about.education.title': 'Formation',
    'about.education.degree': 'Bootcamp Full-Stack MERN',
    'about.education.institution': 'GoMyCode',
    'about.education.period': '2024 - 2025',
    'about.education.description':
      'Maîtrise de MongoDB, Express.js, React.js, Node.js et techniques avancées Next.js pour des applications web scalables.',
    'about.cta.title': 'Envie de travailler ensemble ?',
    'about.cta.text':
      'Je suis toujours à la recherche de projets stimulants. Discutons de la meilleure façon de donner vie à vos idées.',
    'about.cta.button': 'Me contacter',

    // Projects page
    'projects.seo.title': 'Projets',
    'projects.seo.description':
      'Découvrez mon portfolio de projets web : sites, applications et autres réalisations.',
    'projects.title': 'Mes projets',
    'projects.description':
      'Découvrez mes travaux récents. Chaque projet représente un défi unique et met en avant différentes facettes de mes compétences.',
    'projects.filter.all': 'Tous les projets',
    'projects.filter.featured': 'Mises en avant',
    'projects.empty.title': 'Aucun projet trouvé',
    'projects.empty.text':
      "Aucun projet ne correspond au filtre sélectionné. Essayez un autre filtre.",

    // Project detail
    'projectDetail.back': 'Retour aux projets',
    'projectDetail.featuredBadge': 'Projet mis en avant',
    'projectDetail.techTitle': 'Technologies utilisées',
    'projectDetail.liveDemo': 'Voir la démo en ligne',
    'projectDetail.liveVersion': 'Version en ligne',
    'projectDetail.viewSource': 'Voir le code source',
    'projectDetail.otherProjects': 'Autres projets',

    // Project card
    'projectCard.featured': 'Mis en avant',
    'projectCard.liveDemo': 'Démo en ligne',
    'projectCard.liveVersion': 'Version en ligne',
    'projectCard.code': 'Code',
    'projectCard.viewDetails': 'Voir les détails',

    // Skills page
    'skills.seo.title': 'Compétences',
    'skills.seo.description':
      'Découvrez mes compétences techniques en développement web : frontend, backend, design et plus.',
    'skills.title': 'Mes compétences & expertises',
    'skills.description':
      'Au fil de mon parcours, j’ai développé un large éventail de compétences. Voici une vue d’ensemble de mon expertise technique dans différents domaines du développement web.',
    'skills.category.frontend': 'Développement Frontend',
    'skills.category.frontendDesc':
      'Création d’interfaces utilisateur réactives et interactives',
    'skills.category.backend': 'Développement Backend',
    'skills.category.backendDesc':
      'Mise en place d’applications serveur et d’APIs robustes',
    'skills.category.design': 'Design',
    'skills.category.designDesc':
      'Conception d’expériences utilisateur esthétiques et fonctionnelles',
    'skills.category.tools': 'Outils & Méthodologies',
    'skills.category.toolsDesc':
      'Soutien du processus de développement avec les bons outils',
    'skills.category.productivity': 'Outils de productivité',
    'skills.category.productivityDesc':
      'Optimisation du travail quotidien avec des outils bureautiques polyvalents',
    'skills.learning.title': 'En cours d’apprentissage',
    'skills.learning.intro':
      'Je crois en un apprentissage continu et en la veille technologique. Voici quelques domaines sur lesquels je me concentre actuellement :',
    'skills.learning.web3.title': 'Technologies Web 3.0',
    'skills.learning.web3.text':
      'Exploration de la blockchain, des smart contracts et des applications décentralisées.',
    'skills.learning.animation.title': 'Animations avancées',
    'skills.learning.animation.text':
      'Maîtrise d’animations complexes et de micro-interactions pour une meilleure UX.',
    'skills.learning.ai.title': 'Intégration de l’IA',
    'skills.learning.ai.text':
      'Intégration de fonctionnalités IA dans des applications web pour des expériences plus intelligentes.',

    // Contact page
    'contact.seo.title': 'Contact',
    'contact.seo.description':
      'Contactez-moi pour une collaboration, un projet ou toute question.',
    'contact.title': 'Restons en contact',
    'contact.intro':
      'Vous avez un projet en tête ou souhaitez évoquer une collaboration ? Je serai ravie d’échanger avec vous. Remplissez le formulaire ci-dessous ou utilisez l’un des moyens de contact proposés.',
    'contact.info.title': 'Informations de contact',
    'contact.info.email': 'Email',
    'contact.info.phone': 'Téléphone',
    'contact.info.location': 'Localisation',
    'contact.social.title': 'Me suivre sur les réseaux',
    'contact.availability.title': 'Disponibilité',
    'contact.availability.text':
      'Actuellement disponible pour des missions freelance et des opportunités à temps plein.',
    'contact.availability.responseTime': 'Délai de réponse :',
    'contact.availability.responseTimeValue': 'Sous 24 heures',
    'contact.faq.title': 'Questions fréquentes',
    'contact.faq.q1': 'Quels services proposez-vous ?',
    'contact.faq.a1':
      'Je suis spécialisée en développement web full-stack : frontend, backend, design responsive, intégration d’API et systèmes de gestion de contenu.',
    'contact.faq.q2': 'Quel est le délai moyen pour un projet ?',
    'contact.faq.a2':
      'La durée dépend de la complexité et du périmètre du projet. Un site simple peut prendre 2 à 3 semaines, tandis qu’une application complexe peut nécessiter 2 à 3 mois. Un planning détaillé est fourni dès la phase de cadrage.',
    'contact.faq.q3': 'Proposez-vous un suivi ou de la maintenance ?',
    'contact.faq.a3':
      'Oui, je propose des services de maintenance et de support pour garantir la sécurité, la stabilité et la mise à jour de votre site ou application.',
    'contact.faq.q4': 'Comment sont gérées les révisions de projet ?',
    'contact.faq.a4':
      'Le processus inclut des points réguliers et des retours fréquents. En général, jusqu’à trois cycles de révision sont inclus afin d’assurer votre entière satisfaction.',

    // Contact form
    'form.getInTouch': 'Me contacter',
    'form.sending': 'Envoi du message...',
    'form.sendAnother': 'Envoyer un autre message',
    'form.success':
      'Message envoyé avec succès ! Je vous répondrai dans les plus brefs délais.',
    'form.error':
      "Échec de l’envoi du message. Veuillez réessayer plus tard.",
    'form.name': 'Nom',
    'form.name.placeholder': 'Votre nom',
    'form.email': 'Email',
    'form.email.placeholder': 'votre.email@exemple.com',
    'form.subject': 'Objet',
    'form.subject.placeholder': 'Objet de votre message',
    'form.message': 'Message',
    'form.message.placeholder': 'Votre message...',
    'form.submit': 'Envoyer le message',

    // 404
    'notFound.seo.title': 'Page introuvable',
    'notFound.seo.description':
      "La page que vous recherchez est introuvable.",
    'notFound.title': 'Page introuvable',
    'notFound.text':
      "La page que vous recherchez n’existe pas ou a été déplacée.",
    'notFound.backHome': 'Retour à l’accueil',
    'notFound.goBack': 'Revenir en arrière',

    // Terms
    'terms.seo.title': "Conditions d’utilisation",
    'terms.seo.description':
      'Conditions d’utilisation de ce site portfolio. Comprenez les règles encadrant son utilisation.',
    'terms.title': "Conditions d’utilisation",
    'terms.agreement.title': 'Acceptation des conditions',
    'terms.agreement.text':
      "En accédant à ce site et en l’utilisant, vous acceptez d’être lié(e) par les présentes Conditions d’utilisation et de respecter l’ensemble des lois et réglementations applicables.",
    'terms.license.title': 'Licence d’utilisation',
    'terms.license.text1':
      'Vous êtes autorisé(e) à consulter temporairement les contenus (informations ou logiciels) présents sur ce site pour un usage personnel et non commercial. Il s’agit d’une licence d’utilisation, et non d’un transfert de propriété.',
    'terms.license.text2': 'Dans le cadre de cette licence, il vous est interdit de :',
    'terms.license.item1': 'Modifier ou copier les contenus',
    'terms.license.item2': 'Utiliser les contenus à des fins commerciales',
    'terms.license.item3':
      'Tenter de décompiler ou de rétro‑concevoir tout logiciel présent sur le site',
    'terms.license.item4':
      'Supprimer toute mention de droit d’auteur ou autre mention propriétaire',
    'terms.license.item5':
      'Transférer les contenus à une autre personne',
    'terms.disclaimer.title': 'Avertissement',
    'terms.disclaimer.text':
      "Les contenus de ce site sont fournis « en l’état ». Aucune garantie, explicite ou implicite, n’est donnée, y compris, sans s’y limiter, les garanties de qualité marchande, d’adéquation à un usage particulier ou de non‑violation de droits de propriété intellectuelle.",
    'terms.limitations.title': 'Limitations de responsabilité',
    'terms.limitations.text':
      'En aucun cas, nous ou nos fournisseurs ne pourrons être tenus responsables de dommages (y compris, sans s’y limiter, les pertes de données, de profits ou les interruptions d’activité) résultant de l’utilisation ou de l’impossibilité d’utiliser ce site ou ses contenus.',
    'terms.links.title': 'Liens',
    'terms.links.text':
      'Nous ne contrôlons pas l’intégralité des sites liés au nôtre et déclinons toute responsabilité quant à leur contenu. La présence d’un lien ne signifie pas que nous approuvons le site en question. L’utilisation de ces sites tiers se fait aux risques et périls de l’utilisateur.',
    'terms.modifications.title': 'Modifications',
    'terms.modifications.text':
      'Nous pouvons modifier ces Conditions d’utilisation à tout moment, sans préavis. En continuant d’utiliser ce site, vous acceptez la version en vigueur des présentes conditions.',
    'terms.contact.title': 'Informations de contact',
    'terms.contact.text':
      'Pour toute question concernant ces Conditions d’utilisation, vous pouvez nous écrire à',

    // Privacy
    'privacy.seo.title': 'Politique de confidentialité',
    'privacy.seo.description':
      'Politique de confidentialité de ce site portfolio. Découvrez comment vos données personnelles sont collectées et utilisées.',
    'privacy.title': 'Politique de confidentialité',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.text':
      'Cette Politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations personnelles lors de l’utilisation de ce site. Nous nous engageons à respecter la confidentialité et la sécurité de vos données.',
    'privacy.collect.title': 'Données collectées',
    'privacy.collect.text':
      'Nous collectons les informations que vous nous fournissez directement, notamment :',
    'privacy.collect.item1':
      'Votre nom et votre adresse email lorsque vous nous contactez',
    'privacy.collect.item2':
      'Les messages et échanges transmis via le formulaire de contact',
    'privacy.collect.item3':
      'Des données d’usage et d’analytique relatives à votre navigation',
    'privacy.use.title': 'Utilisation de vos données',
    'privacy.use.text': 'Nous utilisons les données collectées pour :',
    'privacy.use.item1': 'Répondre à vos demandes et messages',
    'privacy.use.item2': 'Améliorer le site et nos services',
    'privacy.use.item3':
      'Vous envoyer des actualités ou informations (avec votre consentement)',
    'privacy.use.item4':
      'Respecter nos obligations légales',
    'privacy.protect.title': 'Protection des données',
    'privacy.protect.text':
      'Nous mettons en œuvre des mesures de sécurité adaptées pour protéger vos informations personnelles contre tout accès, modification, divulgation ou destruction non autorisés. L’accès à vos données est limité aux personnes habilitées.',
    'privacy.rights.title': 'Vos droits',
    'privacy.rights.text': 'Vous disposez notamment du droit de :',
    'privacy.rights.item1': 'Accéder à vos données personnelles',
    'privacy.rights.item2':
      'Demander la rectification des informations inexactes',
    'privacy.rights.item3': 'Demander la suppression de vos données',
    'privacy.rights.item4':
      'Retirer votre consentement à tout moment',
    'privacy.contact.title': 'Contact',
    'privacy.contact.text':
      'Pour toute question concernant cette Politique de confidentialité, vous pouvez nous écrire à',
    'privacy.updates.title': 'Mises à jour',
    'privacy.updates.text':
      'Cette Politique de confidentialité peut être mise à jour ponctuellement. La dernière version en date sera toujours disponible sur cette page, avec la date d’entrée en vigueur indiquée.',
  },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>('en');

  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored === 'en' || stored === 'fr') {
      setLangState(stored);
      return;
    }
    const browser = navigator.language.startsWith('fr') ? 'fr' : 'en';
    setLangState(browser as Language);
  }, []);

  const setLang = useCallback((value: Language) => {
    setLangState(value);
    window.localStorage.setItem(STORAGE_KEY, value);
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === 'en' ? 'fr' : 'en');
  }, [lang, setLang]);

  const dict = useMemo(() => resources[lang], [lang]);

  const t = useCallback(
    (key: string): string => {
      return dict[key] ?? key;
    },
    [dict]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang,
      t,
    }),
    [lang, setLang, toggleLang, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};

export const useTranslation = () => {
  const { t, lang, toggleLang, setLang } = useLanguage();
  return { t, lang, toggleLang, setLang };
};


